import { useQuery } from '@tanstack/react-query'
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { useCmMe } from '@/api/hooks/useCmMe'
import {
  asAnalyticsMemberDetail,
  mergeAnalyticsMemberDetail,
} from '@/api/map/memberDetail'
import {
  buildMemberDetailView,
  type MemberDetailView,
} from '@/features/member/memberAccount'

export type MemberDetailRange = { from: string; to: string }

async function loadMemberDetail(
  id: string,
  rootId: string,
  range: MemberDetailRange,
): Promise<MemberDetailView | null> {
  const [treeRes, agentRes] = await Promise.all([
    cmApi.hierarchyTree(authHeaders(), rootId),
    cmApi.hierarchyAgent(authHeaders(), id),
  ])
  const tree = treeRes.nodes ?? []
  const node = agentRes.node
  if (!node) return null
  const base = buildMemberDetailView(tree, node, rootId)
  if (!base) return null
  try {
    const live = await cmApi.memberDetail(authHeaders(), id, range.from, range.to)
    const analytics = asAnalyticsMemberDetail(live)
    if (!analytics) return base
    return mergeAnalyticsMemberDetail(base, analytics)
  } catch (err) {
    if (err instanceof CmApiHttpError && (err.status === 404 || err.status === 503)) {
      return base
    }
    // Soft-fail analytics — hierarchy Overview still works.
    return base
  }
}

export function useMemberDetail(id: string | undefined, range: MemberDetailRange) {
  const me = useCmMe()
  const self = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })
  const rootId = me.data?.caller?.agentId ?? self.data?.node.id
  return useQuery({
    queryKey: ['cm', 'member-detail', id, rootId, range.from, range.to],
    queryFn: () => loadMemberDetail(id!, rootId!, range),
    enabled: Boolean(id && rootId),
  })
}
